import { SIGNIFYD_ERROR_EVENT, SIGNIFYD_SCRIPT_ID, SIGNIFYD_SCRIPT_SRC } from './config.js';
import type { SignifydHooks } from './ports.js';

export interface SignifydLoaderDeps {
	/** Source of the `data-order-session-id` (the shared payment-session id). */
	getSessionId(): string | null | undefined;
	/** Telemetry side effects (Sentry lives in the host adapter). */
	hooks?: SignifydHooks;
}

export interface SignifydLoader {
	/** Injects the Signifyd script into `<head>` (idempotent, no-op on SSR/no-id). */
	load(): void;
	/** Removes the script and detaches the `sigScriptError` listener. */
	remove(): void;
}

/**
 * Creates a Signifyd device-profiling loader. Injection is idempotent (guards on
 * the existing `#sig-api` node) so it survives A/B variant remounts, and no-ops
 * during SSR or when no session id is available.
 * @param deps Session-id source + telemetry hooks.
 * @returns The loader.
 */
export function createSignifydLoader(deps: SignifydLoaderDeps): SignifydLoader {
	const { getSessionId, hooks } = deps;
	let errorListener: ((event: Event) => void) | null = null;

	const load = (): void => {
		if (typeof document === 'undefined') return;
		if (document.getElementById(SIGNIFYD_SCRIPT_ID)) return;

		const sessionId = getSessionId();
		if (!sessionId) {
			// No session id → skip rather than load a script Signifyd can't correlate.
			hooks?.onSkip?.({ reason: 'no payment session id' });
			return;
		}

		errorListener = (event: Event): void => {
			const detail = (event as CustomEvent)?.detail;
			hooks?.onSigScriptError?.({ detail });
		};
		document.addEventListener(SIGNIFYD_ERROR_EVENT, errorListener);

		const script = document.createElement('script');
		script.id = SIGNIFYD_SCRIPT_ID;
		script.src = SIGNIFYD_SCRIPT_SRC;
		script.async = true;
		script.setAttribute('data-order-session-id', sessionId);
		script.addEventListener('load', () => hooks?.onScriptLoaded?.());
		script.addEventListener('error', () => hooks?.onScriptError?.({ src: SIGNIFYD_SCRIPT_SRC }));

		document.head.appendChild(script);
	};

	const remove = (): void => {
		if (typeof document === 'undefined') return;
		if (errorListener) {
			document.removeEventListener(SIGNIFYD_ERROR_EVENT, errorListener);
			errorListener = null;
		}
		document.getElementById(SIGNIFYD_SCRIPT_ID)?.remove();
	};

	return { load, remove };
}
